import { Botao } from '../../../../components/Botao'
import { TEXTOS } from '../../../../lib/glossario'
import type { ParecerDaIa } from '../../../../types/chamado'
import { rotuloDoValor } from './opcoes'
import { CAMPOS_PROPONIVEIS, propostaDe, type CampoProponivel } from './proposta'

const ROTULO_DA_PERGUNTA: Record<CampoProponivel, string> = {
  natureza: TEXTOS.marcacao.natureza.rotulo,
  prazo: TEXTOS.marcacao.prazo.rotulo,
  criticidade: TEXTOS.marcacao.criticidade.rotulo,
  conformidade: TEXTOS.marcacao.conformidade.rotulo,
  aceite: TEXTOS.marcacao.aceite.rotulo,
}

/**
 * O que a IA sugeriu, numa caixa só, com o botão de usar todas as sugestões.
 *
 * Pedido do teste de uso 3 (25/09/2026): conferir a sugestão pergunta a pergunta, espalhada pelo
 * formulário, custava rolar a tela inteira. O resumo junta as respostas propostas para a leitura de
 * uma vez, e o botão as coloca no formulário — inclusive por cima de um julgamento anterior, que é o
 * caso em que o preenchimento automático não age.
 *
 * O botão NÃO salva. Salvar continua sendo o clique do fiscal, depois de ler (Regra nº 3: a IA é só
 * análise, e nenhum caminho dela grava julgamento); a trilha registra quais sugestões ele manteve.
 *
 * @param props o parecer da IA e o que fazer com cada sugestão
 * @returns a caixa, ou nada quando o parecer não propõe resposta nenhuma
 */
export function ResumoDaSugestao({
  parecer,
  aoUsar,
}: {
  parecer: ParecerDaIa | null
  aoUsar: (campo: CampoProponivel, valor: string) => void
}) {
  const t = TEXTOS.marcacao.sugestaoDaIa
  const propostas = CAMPOS_PROPONIVEIS.flatMap((campo) => {
    const proposta = propostaDe(parecer, campo)
    return proposta ? [{ campo, valor: proposta.valorNoFormulario }] : []
  })
  if (propostas.length === 0) {
    return null
  }

  return (
    <section
      aria-label={t.resumo}
      className="max-w-prose rounded-lg border border-niteroi-200 bg-niteroi-50/60 px-3 py-3 text-sm text-slate-800"
    >
      <h3 className="text-sm font-semibold text-niteroi-900">{t.resumo}</h3>
      <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[auto,1fr]">
        {propostas.map(({ campo, valor }) => (
          <div key={campo} className="contents">
            <dt className="text-xs text-slate-600">{ROTULO_DA_PERGUNTA[campo]}</dt>
            <dd className="text-xs font-medium text-slate-900">{rotuloDoValor(campo, valor)}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Botao tamanho="pequeno" onClick={() => propostas.forEach(({ campo, valor }) => aoUsar(campo, valor))}>
          {t.usarTodas}
        </Botao>
        <span className="text-xs text-slate-600">{t.usarTodasAjuda}</span>
      </div>
    </section>
  )
}
